import { useQuery, useMutation, useConvex } from "convex/react";
import { api } from "../../api";
import { useToast } from "@/hooks/use-toast";
import type { AppMode } from "@/types/convex";
import type { InstalledAppMeta } from "@/components/InstalledAppsReviewModal";

/**
 * Hook to manage device app mode (whitelist, blacklist, categories).
 * 
 * Key principle: Mode changes ONLY update the appMode field - they never modify
 * appFilter, allowedApps, or blockedApps. This allows settings to persist
 * when switching between modes.
 * 
 * Priority chain:
 * 1. Device appMode (if set, used directly)
 * 2. Level appMode (if device doesn't override, inherit from level)
 * 3. Legacy inference (if neither set, inferred from appFilter for backwards compatibility)
 * 
 * Sidebar visibility by mode:
 * - whitelist: Settings, Whitelist only
 * - blacklist: Settings, Blacklist, Installed only
 * - categories: All sections (Settings, Categories, Whitelist, Blacklist, All Apps, Installed)
 */
export function useDeviceAppMode(deviceId: string) {
  const { toast } = useToast();
  const convex = useConvex();
  
  // Get device details which includes appMode from deviceConfig
  const deviceData = useQuery(
    api.devices.get.details, 
    deviceId ? { device: deviceId as any } : "skip"
  );
  
  // Get device app config to read current appFilter for fallback mode detection
  const deviceAppConfig = useQuery(
    api.devices.apps.getConfig,
    deviceData?.device?.deviceConfig ? { deviceConfig: deviceData.device.deviceConfig } : "skip"
  );
  
  // Mutation for updating device apps - sends appMode directly
  const updateDeviceApps = useMutation(api.devices.apps.update);
  // Mutation for adding apps to catalog
  const addAppToCatalog = useMutation(api.apps.add.app);

  // Expose whitelisted device app IDs for consumers to use
  const deviceWhitelistedAppIds = Object.keys(deviceAppConfig?.allowedApps || {})
    .filter(appId => (deviceAppConfig?.allowedApps as any)?.[appId] === 'device');
  
  /**
   * Determine current app mode.
   * Priority: 
   * 1. Explicit appMode from deviceConfig
   * 2. Inferred from appFilter (fallback for backwards compatibility)
   */
  const getAppMode = (): AppMode => {
    // Read explicit appMode from device config if available
    const explicitMode = (deviceAppConfig as any)?.appMode;
    if (explicitMode) return explicitMode;
    
    // Fallback: infer from appFilter for backwards compatibility
    const appFilter = deviceAppConfig?.appFilter;
    
    // appFilter === true means Apple App Store (blacklist mode)
    if (appFilter === true) {
      return "blacklist";
    }
    
    // Check for whitelist-only marker
    if (typeof appFilter === 'object' && appFilter !== null) {
      const hasWhitelistMarker = Object.keys(appFilter).some(key => 
        key === "whitelist-only" || 
        (typeof appFilter[key] === 'object' && (appFilter[key] as any)?.["whitelist-only"])
      );
      if (hasWhitelistMarker) {
        return "whitelist";
      }
    }
    
    // Default to categories mode
    return "categories";
  };
  
  const appMode = getAppMode();

  /**
   * Check if a category is allowed in the current appFilter.
   * Used by consumers to determine if a whitelisted app is redundant in categories mode.
   */
  const isCategoryAllowed = (categoryName: string): boolean => {
    const appFilter = deviceAppConfig?.appFilter;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const appFilterAny = appFilter as any;
    if (!appFilterAny || appFilterAny === true || appFilterAny === false) return true;
    const entry = (appFilterAny as Record<string, any>)[categoryName];
    if (!entry) return true; // Not configured = allowed by default
    const tagFilter = entry.tagFilter;
    if (tagFilter === true || (typeof tagFilter === 'object' && tagFilter !== null)) return true;
    return false;
  };
  
  /**
   * Set the app mode.
   * 
   * Sends appMode directly to backend - does NOT modify appFilter, allowedApps, or blockedApps.
   * Whitelist pruning (when switching to categories) is handled by DeviceAppsTab
   * which has access to app category data.
   */
  const setAppMode = async (mode: AppMode) => {
    if (!deviceId) {
      toast({
        title: "Error",
        description: "Device ID not available",
        variant: "destructive",
      });
      return;
    }
    
    try {
      await updateDeviceApps({
        device: deviceId,
        appMode: mode,
      } as any);
      
      toast({
        title: "App Mode Updated",
        description: `Device app mode set to ${mode}.`,
      });
    } catch (error) {
      console.error("Error updating app mode:", error);
      toast({
        title: "Error",
        description: "Failed to update app mode",
        variant: "destructive",
      });
    }
  };

  /**
   * Set app mode to categories AND prune the whitelist.
   * Removes device-whitelisted apps whose category is already allowed by the category filter.
   * Takes appCategories (array of {name, appId} mappings from the caller).
   */
  const setAppModeToCategoriesWithPrune = async (
    appIdToCategory: Record<string, string>
  ) => {
    if (!deviceId) {
      toast({
        title: "Error",
        description: "Device ID not available",
        variant: "destructive",
      });
      return;
    }

    try {
      // Filter out whitelisted apps whose category is already allowed
      const prunedAllowedApps = deviceWhitelistedAppIds.filter(appId => {
        const category = appIdToCategory[appId];
        if (!category) return true; // Keep if we don't know the category
        return !isCategoryAllowed(category); // Keep only if category is blocked
      });

      const removed = deviceWhitelistedAppIds.length - prunedAllowedApps.length;

      await updateDeviceApps({
        device: deviceId,
        appMode: "categories",
        allowedApps: prunedAllowedApps as any[],
        blockedApps: Object.keys(deviceAppConfig?.blockedApps || {})
          .filter(appId => (deviceAppConfig?.blockedApps as any)?.[appId] === 'device') as any[],
        appFilter: Object.fromEntries(
          Object.entries((deviceAppConfig?.appFilter as Record<string, any>) || {})
            .filter(([_, config]) => config && typeof config === 'object' && 'tagFilter' in config)
            .map(([key, config]) => [key, config.tagFilter])
        ),
      } as any);

      toast({
        title: "Switched to Categories",
        description: removed > 0
          ? `Removed ${removed} redundant app${removed !== 1 ? 's' : ''} from whitelist (already allowed by category).`
          : "Switched to categories mode.",
      });
    } catch (error) {
      console.error("Error switching to categories with prune:", error);
      toast({
        title: "Error",
        description: "Failed to switch mode",
        variant: "destructive",
      });
    }
  };
  
  /**
   * Set app mode to whitelist AND seed the allowedApps list with the given apps.
   * Resolves each app's bundleId to an internal Convex Id<"apps"> via catalog query.
   * If an app is not in the catalog, it is added automatically before whitelisting.
   */
  const setAppModeToWhitelistWithImport = async (selectedApps: InstalledAppMeta[]) => {
    if (!deviceId) {
      toast({ title: "Error", description: "Device ID not available", variant: "destructive" });
      return;
    }

    try {
      const resolvedIds: string[] = [];

      for (const app of selectedApps) {
        // Try to find existing catalog entry
        const result = await convex.query(api.apps.list.all, {
          query: app.bundleId,
          paginationOpts: { numItems: 5, cursor: null },
        });
        const match = result.page.find((a: any) => a.bundleId === app.bundleId);

        if (match) {
          resolvedIds.push(match.id);
        } else {
          // Not in catalog — add it and use the returned ID directly
          const newId = await addAppToCatalog({
            bundleId: app.bundleId,
            name: app.name || app.bundleId,
            iconUrl: app.iconUrl || "",
            trackId: app.trackId || app.bundleId,
            category: "",
            tags: [],
            ...(app.version ? { version: app.version } : {}),
          });
          if (newId) resolvedIds.push(newId);
        }
      }

      // Merge with already-whitelisted device IDs (dedup)
      const existingSet = new Set(deviceWhitelistedAppIds);
      const merged = [...deviceWhitelistedAppIds, ...resolvedIds.filter(id => !existingSet.has(id))];

      await updateDeviceApps({
        device: deviceId,
        appMode: "whitelist",
        allowedApps: merged as any[],
        blockedApps: Object.keys(deviceAppConfig?.blockedApps || {})
          .filter(appId => (deviceAppConfig?.blockedApps as any)?.[appId] === 'device') as any[],
        appFilter: Object.fromEntries(
          Object.entries((deviceAppConfig?.appFilter as Record<string, any>) || {})
            .filter(([_, config]) => config && typeof config === 'object' && 'tagFilter' in config)
            .map(([key, config]) => [key, config.tagFilter])
        ),
      } as any);

      toast({
        title: "Switched to Whitelist",
        description: resolvedIds.length > 0
          ? `${resolvedIds.length} app${resolvedIds.length !== 1 ? 's' : ''} added to whitelist.`
          : "Switched to Whitelist mode.",
      });
    } catch (error) {
      console.error("Error switching to whitelist with import:", error);
      toast({ title: "Error", description: "Failed to switch mode", variant: "destructive" });
    }
  };

  return {
    appMode,
    setAppMode,
    setAppModeToCategoriesWithPrune,
    setAppModeToWhitelistWithImport,
    isCategoryAllowed,
    deviceWhitelistedAppIds,
    deviceAppConfig,
    isLoading: deviceData === undefined || deviceAppConfig === undefined,
  };
}
